import React, { useEffect, useState, useRef, useMemo, useCallback, useContext } from "react";
import { secureGet, secureDelete } from "../../config/api";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

import { formatDate } from "../../utils/format";
import { PrimaryButton } from "../../components/PrimaryButton";
import LoadingLogo from "../../components/LoadingLogo";
import Rows from "../../components/Rows";
import Filter from "../../components/Filter";
import PageScroller from "../../components/PageScroller";
import DeletePopup from "../../components/DeletePopup";
import TransferRequestDetails from "./TransferRequestDetails";
import SnackbarNotification from '../../components/SnackbarNotification';
import UserContext from "../../Shared/UserContext";

import AddIcon from "../../icons/plusB.svg";
import deleteIcon from "../../icons/trashR.svg";
import editIcon from "../../icons/edit.svg";
import arrowIcon from "../../icons/chevron-rightP.svg";

const TransferRequests = () => {
  const [allRequestsData, setAllRequestsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPageData, setCurrentPageData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    date: { start: "", end: "" },
    stockSource: "",
    stockDestination: "",
    sortOrder: "true",
  });
  const [repos, setRepos] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const history = useHistory();
  const [isDeletePopupVisible, setDeletePopupVisible] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const { t } = useTranslation();
  const topRef = useRef();
  const location = useLocation();
  const { state: { role } } = useContext(UserContext);

  useEffect(() => {
    if (location.state?.showSnackbar) {
      showSnackbar(location.state.snackbarMessage, location.state.snackbarSeverity);
      history.replace({ ...location, state: undefined });
    }
  }, [location, history]);

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const showSnackbar = (message, severity = 'success', manual = false) => {
    setSnackbar({ open: true, message, severity, manual });
  };

  const columns = [
    {
      label: "Date",
      key: "createdAt",
      render: (date) => formatDate(date),
    },
    {
      label: t(`columns.by`),
      key: "user",
      render: (user) => `${user.firstName} ${user.lastName}`,
    },
    {
      label: t(`columns.sourceRepo`),
      key: "sourceRepo",
      render: (repo) => repo?.name || "N/A",
    },
    {
      label: "",
      key: "arrowIcon",
      render: () => (
        <div className="flex items-center justify-center">
          <img src={arrowIcon} alt="Arrow" className="w-5 h-5" />
        </div>
      ),
    },
    {
      label: t(`columns.destRepo`),
      key: "destRepo",
      render: (repo) => repo?.name || "N/A",
    },
    {
      label: t(`columns.status`),
      key: "treated",
      render: (treated) => {
        const bgColor = treated ? "#06D6A0" : "#FEA26F"; 
        const text = treated ? "Validé" : "En attente";

        return (
          <div
            className="text-white text-[16px] font-semibold px-2 py-1 rounded-[12px] text-center whitespace-nowrap"
            style={{ backgroundColor: bgColor }}
          >
            {text}
          </div>
        );
      },
    },
    {
      key: "actions",
      render: (_, row) => (
        <div className="ml-10 flex gap-6">
          <div
            className="bg-white rounded-[12px] w-8 h-8 flex items-center justify-center cursor-pointer "
            style={{ boxShadow: "0px 6px 26px #626B7D12" }}
            onClick={() => handleEditClick(row)}
          >
            <img src={editIcon} alt="Edit" className="w-4 h-4" />
          </div>

          <div
            className="bg-white rounded-[12px] w-8 h-8 flex items-center justify-center cursor-pointer "
            style={{ boxShadow: "0px 6px 26px #626B7D12" }}
            onClick={(event) => {
              event.stopPropagation();
              handleDeleteClick(row);
            }}          >
            <img src={deleteIcon} alt="Delete" className="w-4 h-4" />
          </div>
        </div>
      ),
    },
  ];

  const filterOptions = [
    { name: "date", type: "daterange" },
    {
      name: "stockSource",
      label: t(`filters.requests.sourceRepo`),
      type: "dropdown",
      options: repos.map((r) => ({ value: r.name, label: r.name })),
    },
    {
      name: "stockDestination",
      label: t(`filters.requests.destRepo`),
      type: "dropdown",
      options: repos.map((r) => ({ value: r.name, label: r.name })),
    },
    {
      name: "sortOrder",
      label: t(`filters.requests.sortBy`),
      type: "dropdown",
      options: [
        { value: "true", label: t(`options.oldest`) },
        { value: "false", label: t(`options.newest`) },
      ],
    },
  ];

  const handleDeleteClick = (item) => {
    setItemToDelete(item);
    setDeletePopupVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleting(true);
    try {
      const deleteUrl = `/repos/transfer/draft/delete/${itemToDelete.id}`;
      await secureDelete(deleteUrl, showSnackbar);
      setFilteredData((prevData) =>
        prevData.filter((item) => item.id !== itemToDelete.id)
      );
      setCurrentPageData((prevData) =>
        prevData.filter((item) => item.id !== itemToDelete.id)
      );
      setDeletePopupVisible(false);
      setItemToDelete(null);
      showSnackbar(t(`confirmation.requestDelete`), 'success');
    } catch (error) {
      console.error("Error deleting purchase:", error);
    } finally {
      setDeleting(false);
      setDeletePopupVisible(false);
      setItemToDelete(null);
    }
  };

  const handleEditClick = (item) => {
    history.push({
      pathname: `/transfer-requests/modify/${item.id}`,
      state: { request: item },
    });
  };

  const fetchDropdownOptions = useCallback(async () => {
    try {
      const reposResponse = await secureGet("/repos", showSnackbar, { signal });
      setRepos(reposResponse.data);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error fetching dropdown options:", error);
      }
    }
  }, [signal]);

  const fetchAllRequestsData = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const isCommercial = role === "ROLE_COMMERCIAL";
    setLoading(true);
    let url;
    try {
      if (isCommercial) {
        url = `/repos/transfer/draft/mine/${filters.sortOrder}`;
      } else {
        url = `/repos/transfer/draft/web/${filters.sortOrder}`;
      }
      const response = await secureGet(url, showSnackbar, { signal });
      setAllRequestsData(response.data);
      setFilteredData(response.data);
      setTotalPages(Math.ceil(response.data.length / pageSize));
      setCurrentPageData(paginateData(response.data, 0, pageSize));
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error fetching transfer requests:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [filters.sortOrder, signal, pageSize]);

  useEffect(() => {
    const abortController = new AbortController();

    fetchDropdownOptions();
    fetchAllRequestsData();
    return () => {
      abortController.abort();
    };
  }, [filters.sortOrder, abortController, fetchDropdownOptions, fetchAllRequestsData]);

  const paginateData = (data, page, size) => {
    const startIndex = page * size;
    const endIndex = startIndex + size;
    return data.slice(startIndex, endIndex);
  };

  useEffect(() => {
    const filtered = allRequestsData.filter((item) => {
      const itemDate = new Date(item.createdAt);
      const startDate = filters.date.start ? new Date(filters.date.start) : null;
      const endDate = filters.date.end ? new Date(filters.date.end) : null;

      const isWithinDateRange =
        (!startDate || itemDate >= startDate) &&
        (!endDate || itemDate <= endDate);

      return (
        isWithinDateRange &&
        (filters.stockSource === "" || item.sourceRepo?.name === filters.stockSource) &&
        (filters.stockDestination === "" || item.destRepo?.name === filters.stockDestination)
      );
    });

    setFilteredData(filtered);
    setTotalPages(Math.ceil(filtered.length / pageSize));
    setCurrentPageData(paginateData(filtered, currentPage, pageSize));
  }, [filters, allRequestsData, currentPage, pageSize]);

  const handleTransferValidated = () => {
    fetchAllRequestsData();
  }


  const handleFilterChange = (filterName, filterValue) => {
    setFilters((prev) => {
      const updatedFilters = { ...prev };
      if (filterName === "date") {
        updatedFilters.date = {
          startDate: filterValue.startDate || "",
          endDate: filterValue.endDate || "",
        };
      } else {
        updatedFilters[filterName] = filterValue;
      }
      return updatedFilters;
    });
  };

  const resetFilters = () => {
    setFilters({
      date: { start: "", end: "" },
      stockSource: "",
      stockDestination: "",
      sortOrder: "true",
    });
    setCurrentPage(0);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setCurrentPageData(paginateData(filteredData, page, pageSize));
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(0);
    setTotalPages(Math.ceil(filteredData.length / newPageSize));
    setCurrentPageData(paginateData(filteredData, 0, newPageSize));
  };

  const handleRequestPopup = (row) => setSelectedRequest(row);


  return (
    <div ref={topRef}>
      <Helmet>
        <title>GrayFox - {t("titles.requests")}</title>
      </Helmet>
      <div className="flex items-center mb-[15px] justify-between">
        <h1 className="text-[40px] font-semibold">{t(`titles.requests`)}</h1>
        <div className="ml-auto text-[16px]">
          <PrimaryButton
            label={t(`primaryButtons.add_request`)}
            icon={AddIcon}
            onClick={() => history.push("/transfer-requests/add")}
          />
        </div>
      </div>

      <Filter
        filters={filterOptions}
        onFilterChange={handleFilterChange}
        resetFilters={resetFilters}
        filterValues={filters}
      />

      <div className="table-container" style={{ position: "relative", minHeight: "200px" }}>
        {loading ? (
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.8)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1,
            }}
          >
            <LoadingLogo />
          </div>
        ) : (
          <Rows columns={columns} data={currentPageData} onRowClick={(row) => handleRequestPopup(row)}
          />
        )}
      </div>

      <PageScroller
        pageNumber={currentPage}
        totalPages={totalPages}
        pageSize={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
      />

      {isDeletePopupVisible && (
        <DeletePopup
          open={isDeletePopupVisible}
          onCancel={() => setDeletePopupVisible(false)}
          onConfirm={handleDeleteConfirm}
          loading={deleting}
        />
      )}

      {selectedRequest && (
        <TransferRequestDetails
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onValidated={handleTransferValidated}
          onSave={(msg) => showSnackbar(msg, 'success')}
          onValidate={(msg) => showSnackbar(msg, 'success')}
        />
      )}

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        manual={snackbar.manual}
      />
    </div>
  );
};

export default TransferRequests;
